import React, { useEffect, useRef, useState } from 'react';


import { getFolderColorOptions, resolveFolderColor, createColorReference, createCustomColor } from '@constants/color';
import CancelIcon from '@icon/CancelIcon';
import ColorPaletteIcon from '@icon/ColorPaletteIcon';
import CrossIcon from '@icon/CrossIcon';
import DeleteIcon from '@icon/DeleteIcon';
import DownChevronArrow from '@icon/DownChevronArrow';
import EditIcon from '@icon/EditIcon';
import FolderIcon from '@icon/FolderIcon';
import TickIcon from '@icon/TickIcon';
import useStore from '@store/store';
import {
  ChatHistoryInterface,
  ChatInterface,
  FolderCollection,
} from '@type/chat';
import { Draggable } from 'react-beautiful-dnd';

import ChatHistory from './ChatHistory';
import NewChat from './NewChat';



interface ChatFolderProps {
  folderChats: ChatHistoryInterface[];
  folderId: string;
}

const ChatFolder: React.FC<ChatFolderProps> = ({ folderChats, folderId }) => {
  const folder = useStore((state) => state.folders?.[folderId]);
  const expanded = useStore((state) => state.folders?.[folderId]?.expanded ?? true);
  const theme = useStore((state) => state.theme); // Add theme from store
  
  if (!folder) {
    return null;
  }

  const setChats = useStore((state) => state.setChats);
  const setFolders = useStore((state) => state.setFolders);
  
  const currentFolderColors = getFolderColorOptions(theme);

  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLDivElement>(null);
  const gradientRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);

  const [_folderName, _setFolderName] = useState<string>(folder.name);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isHover, setIsHover] = useState<boolean>(false);
  const [showPalette, setShowPalette] = useState<boolean>(false);
  const [previewColor, setPreviewColor] = useState<string | null>(null);
  const [hoveredColorIndex, setHoveredColorIndex] = useState<number | null>(null);
  
  // Circular color wheel calculations
  const wheelRadius = 60;
  const centerX = wheelRadius + 10;
  const centerY = wheelRadius + 10;
  const wheelSize = (wheelRadius + 10) * 2;
  
  const getColorAngle = (index: number, total: number) => {
    return (index * 360) / total - 90; // Start from top (-90 degrees)
  };
  
  const getMouseAngle = (event: React.MouseEvent, rect: DOMRect) => {
    const centerXPos = rect.left + centerX;
    const centerYPos = rect.top + centerY;
    const mouseX = event.clientX - centerXPos;
    const mouseY = event.clientY - centerYPos;
    
    let angle = Math.atan2(mouseY, mouseX) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    return angle;
  };
  
  const getColorIndexFromAngle = (angle: number, total: number) => {
    const segmentSize = 360 / total;
    return Math.floor((angle + segmentSize / 2) % 360 / segmentSize);
  };
  
  const handleWheelMouseMove = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left - centerX;
    const mouseY = event.clientY - rect.top - centerY;
    const distance = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
    
    // Only trigger if mouse is within the wheel area
    if (distance <= wheelRadius && distance >= 20) {
      const angle = getMouseAngle(event, rect);
      const colorIndex = getColorIndexFromAngle(angle, currentFolderColors.length);
      
      if (colorIndex !== hoveredColorIndex) {
        setHoveredColorIndex(colorIndex);
        handleColorPreview(colorIndex);
      }
    } else {
      setHoveredColorIndex(null);
      setPreviewColor(null);
    }
  };
  
  const handleWheelClick = (_event: React.MouseEvent) => {
    if (hoveredColorIndex !== null) {
      handleColorSelect(hoveredColorIndex);
    }
  };
  
  // Use preview color if hovering, otherwise use actual resolved color
  const displayColor = previewColor || (folder.color ? resolveFolderColor(folder.color, theme) : undefined);
  
  const handleColorPreview = (colorIndex: number) => {
    const palette = getFolderColorOptions(theme);
    setPreviewColor(palette[colorIndex] ?? null);
  };
  
  const handleColorPreviewEnd = () => {
    setPreviewColor(null);
  };
  
  const handleColorSelect = (colorIndex: number) => {
    updateColor(colorIndex);
    setPreviewColor(null); // Clear preview after selection
  };

  const editTitle = () => {
    const updatedFolders: FolderCollection = JSON.parse(
      JSON.stringify(useStore.getState().folders)
    );
    const currentFolder = updatedFolders[folderId];
    if (!currentFolder) {
      return;
    }

    currentFolder.name = _folderName;
    setFolders(updatedFolders);
    setIsEdit(false);
  };

  const deleteFolder = () => {
    const updatedChats: ChatInterface[] = JSON.parse(
      JSON.stringify(useStore.getState().chats)
    );
    updatedChats.forEach((chat: ChatInterface) => {
      if (chat.folder === folderId) delete chat.folder;
    });
    setChats(updatedChats);

    const updatedFolders: FolderCollection = JSON.parse(
      JSON.stringify(useStore.getState().folders)
    );
    delete updatedFolders[folderId];
    setFolders(updatedFolders);

    setIsDelete(false);
  };

  const updateColor = (colorIndex?: number, customHex?: string) => {
    const updatedFolders: FolderCollection = JSON.parse(
      JSON.stringify(useStore.getState().folders)
    );
    const currentFolder = updatedFolders[folderId];
    if (!currentFolder) {
      return;
    }
    
    if (customHex) {
      // Custom color
      currentFolder.color = createCustomColor(customHex);
    } else if (colorIndex !== undefined) {
      // Reference color
      currentFolder.color = createColorReference(colorIndex);
    } else {
      // Remove color
      delete currentFolder.color;
    }
    
    setFolders(updatedFolders);
    setShowPalette(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      editTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCross();
    }
  };

  const handleTick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (isEdit) editTitle();
    else if (isDelete) deleteFolder();
  };

  const handleCross = () => {
    setIsDelete(false);
    setIsEdit(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer) {
      e.stopPropagation();
      setIsHover(false);

      // expand folder on drop
      const updatedFolders: FolderCollection = JSON.parse(
        JSON.stringify(useStore.getState().folders)
      );
      const currentFolder = updatedFolders[folderId];
      if (currentFolder) {
        currentFolder.expanded = true;
      }
      setFolders(updatedFolders);

      // update chat folderId to new folderId
      const chatIndex = Number(e.dataTransfer.getData('chatIndex'));
      const updatedChats: ChatInterface[] = JSON.parse(
        JSON.stringify(useStore.getState().chats)
      );
      const currentChat = updatedChats[chatIndex];
      if (currentChat) {
        currentChat.folder = folderId;
      }
      setChats(updatedChats);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHover(true);
  };

  const handleDragLeave = () => {
    setIsHover(false);
  };

  const toggleExpanded = () => {
    const updatedFolders: FolderCollection = JSON.parse(
      JSON.stringify(useStore.getState().folders)
    );
    const currentFolder = updatedFolders[folderId];
    if (!currentFolder) {
      return;
    }

    currentFolder.expanded = !currentFolder.expanded;
    setFolders(updatedFolders);
  };

  useEffect(() => {
    if (inputRef && inputRef.current) inputRef.current.focus();
  }, [isEdit]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        paletteRef.current &&
        !paletteRef.current.contains(event.target as Node)
      ) {
        setShowPalette(false);
      }
    };

    if (showPalette) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [paletteRef, showPalette]);

  return (
    <Draggable draggableId={folderId} index={folder.order}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <div
            className={`w-full transition-colors group/folder ${isHover ? 'bg-gray-800/40' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div
              style={{ background: displayColor || '' }}
              className={`${
                displayColor ? '' : 'hover:bg-gray-500/10'
              } transition-colors flex py-0 pl-2 pr-1 items-center gap-3 relative rounded-md break-all cursor-pointer parent-sibling text-gray-800 dark:text-gray-100`}
              onClick={toggleExpanded}
              ref={folderRef}
              onMouseEnter={() => {
                if (displayColor && folderRef.current)
                  folderRef.current.style.background = `${displayColor}dd`;
                if (gradientRef.current) gradientRef.current.style.width = '0px';
              }}
              onMouseLeave={() => {
                if (displayColor && folderRef.current)
                  folderRef.current.style.background = displayColor;
                if (gradientRef.current) gradientRef.current.style.width = '1rem';
              }}
            >
                <div {...provided.dragHandleProps}>
                  <FolderIcon className='h-4 w-4' />
                </div>
                <div className='flex-1 text-ellipsis max-h-5 overflow-hidden break-all relative select-none'>
                {isEdit ? (
                  <input
                    type='text'
                    className='focus:outline-blue-600 text-sm border-none bg-transparent p-0 m-0 w-full select-text'
                    value={_folderName}
                    onChange={(e) => {
                      _setFolderName(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={handleKeyDown}
                    ref={inputRef}
                  />
                ) : (
                  _folderName
                )}
                {isEdit || (
                  <div
                    ref={gradientRef}
                    className='absolute inset-y-0 right-0 w-4 z-10 transition-all'
                    style={{
                      background:
                        displayColor &&
                        `linear-gradient(to left, ${
                          displayColor || 'var(--color-900)'
                        }, rgb(32 33 35 / 0))`,
                    }}
                  />
                )}
              </div>
              <div
                className='flex text-gray-300'
                onClick={(e) => e.stopPropagation()}
              >
                {isDelete || isEdit ? (
                  <>
                    <button className='p-1 hover:text-white' onClick={handleTick}>
                      <TickIcon />
                    </button>
                    <button className='p-1 hover:text-white' onClick={handleCross}>
                  {isDelete && <CancelIcon />}
                  {isEdit && <CrossIcon />}
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className='relative md:hidden group-hover/folder:md:inline'
                      ref={paletteRef}
                    >
                      <button
                        className='p-1'  // Remove hover:text-white
                        onClick={() => {
                          setShowPalette((prev) => !prev);
                        }}
                      >
                        <ColorPaletteIcon />
                      </button>
                      {showPalette && (
                        <div 
                          className='absolute left-0 bottom-0 translate-y-full p-3 z-20 bg-white dark:bg-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 shadow-lg'
                          onMouseLeave={handleColorPreviewEnd} // Reset preview when leaving palette
                        >
                          <div className='flex flex-col items-center gap-3'>
                            {/* Circular Color Wheel */}
                            <svg
                              width={wheelSize}
                              height={wheelSize}
                              className='cursor-crosshair'
                              onMouseMove={handleWheelMouseMove}
                              onClick={handleWheelClick}
                              onMouseLeave={() => {
                                setHoveredColorIndex(null);
                                setPreviewColor(null);
                              }}
                            >
                              {/* Background circle */}
                              <circle
                                cx={centerX}
                                cy={centerY}
                                r={wheelRadius}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                className="text-gray-300 dark:text-gray-600"
                              />
                              
                              {/* Color segments */}
                              {currentFolderColors.map((color, index) => {
                                const angle = getColorAngle(index, currentFolderColors.length);
                                const nextAngle = getColorAngle(index + 1, currentFolderColors.length);
                                const isHovered = hoveredColorIndex === index;
                                
                                // Calculate arc path
                                const startAngleRad = (angle * Math.PI) / 180;
                                const endAngleRad = (nextAngle * Math.PI) / 180;
                                const innerRadius = 20;
                                const outerRadius = isHovered ? wheelRadius + 5 : wheelRadius;
                                
                                const x1 = centerX + innerRadius * Math.cos(startAngleRad);
                                const y1 = centerY + innerRadius * Math.sin(startAngleRad);
                                const x2 = centerX + outerRadius * Math.cos(startAngleRad);
                                const y2 = centerY + outerRadius * Math.sin(startAngleRad);
                                
                                const x3 = centerX + outerRadius * Math.cos(endAngleRad);
                                const y3 = centerY + outerRadius * Math.sin(endAngleRad);
                                const x4 = centerX + innerRadius * Math.cos(endAngleRad);
                                const y4 = centerY + innerRadius * Math.sin(endAngleRad);
                                
                                const largeArcFlag = Math.abs(nextAngle - angle) > 180 ? 1 : 0;
                                
                                const pathData = [
                                  `M ${x1} ${y1}`,
                                  `L ${x2} ${y2}`,
                                  `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x3} ${y3}`,
                                  `L ${x4} ${y4}`,
                                  `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}`,
                                  'Z'
                                ].join(' ');
                                
                                return (
                                  <path
                                    key={index}
                                    d={pathData}
                                    fill={color}
                                    stroke="white"
                                    strokeWidth={isHovered ? "2" : "0.5"}
                                    className={`transition-all duration-150 ${isHovered ? 'drop-shadow-lg' : ''}`}
                                    style={{
                                      filter: isHovered ? 'brightness(1.1)' : 'none',
                                      transformOrigin: `${centerX}px ${centerY}px`,
                                      transform: isHovered ? 'scale(1.02)' : 'scale(1)'
                                    }}
                                  />
                                );
                              })}
                              
                              {/* Center indicator */}
                              <circle
                                cx={centerX}
                                cy={centerY}
                                r="8"
                                fill="currentColor"
                                className="text-gray-400 dark:text-gray-500"
                              />
                              <circle
                                cx={centerX}
                                cy={centerY}
                                r="4"
                                fill="white"
                                className="dark:fill-gray-900"
                              />
                            </svg>
                            
                            {/* Remove color button */}
                            <button
                              className='text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
                              onClick={() => {
                                updateColor();
                                setPreviewColor(null);
                              }}
                              onMouseEnter={() => setPreviewColor('')}
                            >
                              Remove Color
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className='p-1 hover:text-white md:hidden group-hover/folder:md:inline'
                      onClick={() => setIsEdit(true)}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className='p-1 hover:bg-gray-500/10 md:hidden group-hover/folder:md:inline'
                      onClick={() => setIsDelete(true)}
                    >
                      <DeleteIcon />
                    </button>
                    <button className='p-1 hover:text-white' onClick={toggleExpanded}>
                      <DownChevronArrow
                        className={`${
                          expanded ? 'rotate-180' : ''
                        } transition-transform`}
                      />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className='ml-3 pl-1 border-l-2 border-gray-200 dark:border-gray-600 flex flex-col gap-1 parent'>
              {expanded && <NewChat folder={folderId} />}
              {expanded &&
                folderChats.map((chat: ChatHistoryInterface) => (
                  <ChatHistory
                    title={chat.title}
                    chatIndex={chat.index}
                    key={`${chat.title}-${chat.index}`}
                    />
                  ))}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default ChatFolder;
